import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, MapPin, Award, Utensils, ArrowRight, ShieldCheck } from "lucide-react";
import CommissionCard from "@/components/CommissionCard";
import { formatINR } from "@/components/ChefCard";
import DietaryBlock from "@/components/DietaryBlock";
import TermsConsent from "@/components/TermsConsent";
import TrialDialog from "@/components/TrialDialog";
import DishGallery from "@/components/DishGallery";
import { payForBooking, fmtLocalDate } from "@/lib/payments";

const CITIES = ["Mumbai", "Bengaluru", "Indore", "Chennai", "Goa", "Dubai"];

export default function ChefDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, requireAuth } = useAuth();
  const [chef, setChef] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [durationMonths, setDurationMonths] = useState(1);
  const [city, setCity] = useState("Mumbai");
  const [startDate, setStartDate] = useState(null);
  const [notes, setNotes] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [dietary, setDietary] = useState({
    dietary_preferences: [], allergies: [], chef_notes: "", kitchen_equipment: [],
  });
  const [bookOpen, setBookOpen] = useState(false);
  const [bookStep, setBookStep] = useState(1); // 1 = date+city+notes, 2 = dietary, 3 = review+pay
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [trialOpen, setTrialOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/chefs/${id}`);
      setChef(r.data.chef);
      setCity(r.data.chef.cities?.[0] || "Mumbai");
    })();
  }, [id]);

  useEffect(() => {
    if (!chef) return;
    (async () => {
      const r = await api.get("/pricing/quote", { params: { monthly_salary: chef.monthly_salary, duration_months: durationMonths } });
      setPricing(r.data);
    })();
  }, [chef, durationMonths]);

  const openBook = () => {
    if (!requireAuth()) return;
    setBookOpen(true);
  };

  const submitBooking = async () => {
    if (!startDate) return toast.error("Pick a start date");
    setSubmitting(true);
    try {
      const r = await api.post("/bookings/private-chef", {
        chef_id: chef.id,
        city,
        start_date: fmtLocalDate(startDate),
        duration_months: durationMonths,
        contact_name: contactName,
        contact_phone: contactPhone,
        notes,
        dietary_preferences: dietary.dietary_preferences,
        allergies: dietary.allergies,
        chef_notes: dietary.chef_notes,
        kitchen_equipment: dietary.kitchen_equipment,
      });
      setCreatedBooking(r.data.booking);
      toast.success("Booking placed — proceed to secure the slot");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setSubmitting(false); }
  };

  const payNow = async () => {
    if (!createdBooking) return;
    setPaying(true);
    try {
      await payForBooking(createdBooking.id);
      setBookOpen(false);
      nav("/my-bookings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Payment failed");
    } finally { setPaying(false); }
  };

  if (!chef) return <div className="max-w-7xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="grid md:grid-cols-5 gap-10">
        <div className="md:col-span-3">
          <div className="aspect-[4/3] overflow-hidden bg-neutral-900 border border-white/10">
            <img src={chef.photo_url} alt={chef.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex items-center gap-3 mt-6">
            <span className="chip"><Star size={10} fill="#F5B041" stroke="#F5B041" /> {chef.rating}</span>
            <span className="chip"><MapPin size={10} /> {chef.cities?.join(" · ")}</span>
            <span className="chip"><Award size={10} /> {chef.experience_yrs} years</span>
            <span className="chip text-[#F5B041] capitalize">{chef.salary_type} roster</span>
          </div>
          <h1 className="font-serif-display text-5xl mt-6">{chef.name}</h1>
          <p className="font-script text-2xl text-[#F5B041] mt-1">{chef.cuisines?.join(" · ")}</p>
          <div className="dotted-sep my-8" />
          <DishGallery images={chef.gallery || []} title={chef.name} />
          <p className="text-neutral-300 leading-relaxed text-lg">{chef.bio}</p>

          <div className="mt-10">
            <div className="small-caps text-neutral-500 mb-4">Signature dishes</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {chef.signature_dishes?.map((d) => (
                <div key={d} className="flex items-center gap-3 border border-white/10 px-4 py-3">
                  <Utensils size={14} className="text-[#F5B041]" />
                  <span className="text-sm">{d}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            <div>
              <div className="small-caps text-neutral-500 mb-2">Languages</div>
              <div className="text-neutral-300">{chef.languages?.join(" · ")}</div>
            </div>
            <div>
              <div className="small-caps text-neutral-500 mb-2">Diet</div>
              <div className="text-neutral-300">{chef.diet_tags?.join(" · ")}</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="sticky top-24">
            <div className="bg-[#141414] border border-white/10 p-7">
              <div className="small-caps text-[10px] text-neutral-500">Monthly salary</div>
              <div className="font-serif-display text-5xl mt-1">₹{formatINR(chef.monthly_salary)}</div>
              <p className="text-neutral-400 text-sm mt-2">
                Billed through Chef Hire. One-time fee of one month's salary + GST. Staff on our payroll.
              </p>

              <div className="dotted-sep my-6" />

              <div>
                <Label className="small-caps text-[10px] text-neutral-500">Engagement</Label>
                <Select value={String(durationMonths)} onValueChange={(v) => setDurationMonths(parseInt(v))}>
                  <SelectTrigger data-testid="duration-select" className="mt-2 rounded-none bg-transparent border-white/10 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-[#141414] border-white/10">
                    {[1, 3, 6, 12].map(n => <SelectItem key={n} value={String(n)} className="rounded-none">{n} month{n > 1 ? "s" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <button onClick={openBook} data-testid="book-chef-btn"
                className="btn-primary w-full mt-6 small-caps text-[11px] inline-flex items-center justify-center gap-2">
                Reserve this chef <ArrowRight size={14} />
              </button>
              <button
                onClick={() => { if (requireAuth()) setTrialOpen(true); }}
                data-testid="book-trial-btn"
                className="w-full mt-3 border border-white/15 hover:border-[#F5B041]/60 h-11 small-caps text-[11px] transition"
              >
                Book a paid trial · from ₹{formatINR(chef.trial_fee || (chef.salary_type === "international" ? 5000 : 2500))}
              </button>
              <p className="text-[10px] text-neutral-500 mt-2">Trials need 2 days' notice and are paid upfront.</p>
              <div className="flex items-center gap-2 mt-4 text-[10px] text-neutral-500">
                <ShieldCheck size={12} className="text-[#F5B041]" /> NDA-bound · Background verified · Replacement guaranteed
              </div>
            </div>

            <div className="mt-6">
              <CommissionCard pricing={pricing} />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={bookOpen} onOpenChange={(o) => { setBookOpen(o); if (!o) setBookStep(1); }}>
        <DialogContent className="max-w-4xl bg-[#0F0F0F] border-white/10 rounded-none p-0 max-h-[92vh] overflow-y-auto" data-testid="book-dialog">
          <div className="p-8">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                {[1, 2, 3].map((s) => (
                  <div key={s} className={`h-1 w-12 ${bookStep >= s ? "bg-[#C04000]" : "bg-white/10"}`} />
                ))}
                <span className="small-caps text-[10px] text-neutral-500 ml-2">
                  {createdBooking ? "Step 3 · Secure" : bookStep === 1 ? "Step 1 · Schedule" : "Step 2 · Dietary & Kitchen"}
                </span>
              </div>
              <DialogTitle className="font-serif-display text-3xl">
                {createdBooking ? "Secure your slot" : `Reserve ${chef.name}`}
              </DialogTitle>
              <p className="text-neutral-400 text-sm mt-1">
                {createdBooking
                  ? "Pay the one-time placement fee (one month's salary + GST) to confirm your booking."
                  : bookStep === 1
                    ? "Pick a start date, city, and add any notes for our team."
                    : "Tailor the chef's brief — dietary preferences, allergies, guest asks, and what's in your kitchen."}
              </p>
            </DialogHeader>

            {!createdBooking && bookStep === 1 && (
              <div className="grid md:grid-cols-2 gap-8 mt-6">
                <div>
                  <Label className="small-caps text-[10px] text-neutral-500">Start date</Label>
                  <div className="mt-2 border border-white/10 p-3 bg-[#141414]">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={{ before: new Date() }}
                      className="rounded-none"
                      data-testid="start-date-picker"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="small-caps text-[10px] text-neutral-500">Your name</Label>
                      <input data-testid="book-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)}
                        placeholder="Full name"
                        className="mt-2 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm focus:border-[#C04000] outline-none" />
                    </div>
                    <div>
                      <Label className="small-caps text-[10px] text-neutral-500">Contact number</Label>
                      <input data-testid="book-contact-phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        className="mt-2 w-full rounded-none bg-transparent border border-white/10 h-11 px-3 text-sm focus:border-[#C04000] outline-none" />
                    </div>
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">City</Label>
                    <Select value={city} onValueChange={setCity}>
                      <SelectTrigger data-testid="book-city" className="mt-2 rounded-none bg-transparent border-white/10 h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-[#141414] border-white/10">
                        {CITIES.map(c => <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="small-caps text-[10px] text-neutral-500">Notes for our team</Label>
                    <Textarea data-testid="book-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Household size, timings, staff who'll assist the chef…"
                      className="mt-2 rounded-none bg-transparent border-white/10 min-h-32" />
                  </div>
                  <CommissionCard pricing={pricing} title="What Chef Hire charges" />
                  <Button onClick={() => startDate ? setBookStep(2) : toast.error("Pick a start date")}
                    data-testid="next-to-dietary-btn"
                    disabled={!startDate}
                    className="w-full rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px]">
                    Next — Dietary & Kitchen <ArrowRight size={14} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {!createdBooking && bookStep === 2 && (
              <div className="mt-6 space-y-8">
                <DietaryBlock value={dietary} onChange={setDietary} testIdPrefix="chef" />
                <TermsConsent checked={agreed} onChange={setAgreed} testId="chef-terms-consent" />
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
                  <Button variant="ghost" onClick={() => setBookStep(1)} data-testid="back-to-schedule-btn"
                    className="rounded-none border border-white/10 h-12 small-caps text-[11px] hover:bg-white/5 flex-1">
                    ← Back
                  </Button>
                  <Button onClick={() => agreed ? submitBooking() : toast.error("Please accept the Terms & Conditions")} disabled={submitting || !agreed}
                    data-testid="submit-booking-btn"
                    className="rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px] flex-[2]">
                    Confirm booking · Continue to payment <ArrowRight size={14} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {createdBooking && (
              <div className="grid md:grid-cols-2 gap-8 mt-6">
                <div className="bg-[#141414] border border-white/10 p-6">
                  <div className="small-caps text-neutral-500 text-[10px]">Booking</div>
                  <div className="font-serif-menu text-xl mt-1">{chef.name}</div>
                  <div className="dotted-sep my-4" />
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-neutral-400">City</span><span>{createdBooking.city}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-400">Start</span><span>{createdBooking.start_date}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-400">Duration</span><span>{createdBooking.duration_months} mo</span></div>
                    <div className="flex justify-between"><span className="text-neutral-400">Status</span><span className="capitalize">{createdBooking.status}</span></div>
                  </div>
                  {(dietary.dietary_preferences?.length > 0 || dietary.allergies?.length > 0 || dietary.kitchen_equipment?.length > 0) && (
                    <>
                      <div className="dotted-sep my-4" />
                      <div className="space-y-3 text-xs">
                        {dietary.dietary_preferences?.length > 0 && (
                          <div><span className="text-neutral-500">Diet: </span><span className="text-[#F5B041]">{dietary.dietary_preferences.join(", ")}</span></div>
                        )}
                        {dietary.allergies?.length > 0 && (
                          <div><span className="text-neutral-500">Allergies: </span><span className="text-[#C04000]">{dietary.allergies.join(", ")}</span></div>
                        )}
                        {dietary.kitchen_equipment?.length > 0 && (
                          <div><span className="text-neutral-500">Equipment: </span><span className="text-white">{dietary.kitchen_equipment.length} item{dietary.kitchen_equipment.length > 1 ? "s" : ""}</span></div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <CommissionCard pricing={pricing} />
                  <Button onClick={payNow} disabled={paying} data-testid="pay-btn"
                    className="w-full mt-4 rounded-none bg-[#C04000] hover:bg-[#A03500] h-12 small-caps text-[11px]">
                    Pay ₹{formatINR(pricing?.chefhire_total)} · Confirm
                  </Button>
                  <p className="text-[10px] text-neutral-500 mt-3 text-center">
                    Payment powered by Cashfree.
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TrialDialog
        chef={chef}
        open={trialOpen}
        onOpenChange={setTrialOpen}
        onBooked={() => nav("/my-bookings")}
      />
    </div>
  );
}
